import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Calendar, MapPin, Star, MessageSquare, AlertCircle, Clock, CheckCircle, XCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useTools } from '../context/ToolsContext';
import Button from '../components/ui/Button';
import Badge from '../components/ui/Badge';

interface BorrowedTool {
  id: string;
  tool: {
    id: string;
    name: string;
    imageUrl: string;
    pricePerDay: number;
    location: string;
    rating: number;
    reviews: number;
  };
  owner: {
    id: string;
    name: string;
    avatar?: string;
    rating: number;
  };
  startDate: string;
  endDate: string;
  status: 'upcoming' | 'active' | 'completed' | 'cancelled' | 'overdue';
  totalPrice: number;
  bookingId: string;
  reviewed: boolean;
  conversationId?: string;
}

const BorrowedTools: React.FC = () => {
  const { user } = useAuth();
  const [filter, setFilter] = useState<'all' | 'active' | 'upcoming' | 'completed'>('all');
  
  // Mock borrowed tools data
  const borrowedTools: BorrowedTool[] = [
    {
      id: 'bt1',
      tool: {
        id: '1',
        name: 'DeWalt Cordless Drill',
        imageUrl: 'https://images.pexels.com/photos/1249611/pexels-photo-1249611.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
        pricePerDay: 15,
        location: 'Midtown, New York',
        rating: 4.8,
        reviews: 24,
      },
      owner: {
        id: 'owner1',
        name: 'David Wallace',
        avatar: 'https://images.pexels.com/photos/220453/pexels-photo-220453.jpeg?auto=compress&cs=tinysrgb&w=60&h=60&dpr=2',
        rating: 4.9,
      },
      startDate: '2024-01-20',
      endDate: '2024-01-22',
      status: 'active',
      totalPrice: 45,
      bookingId: 'booking1',
      reviewed: false,
      conversationId: 'conv1',
    },
    {
      id: 'bt2',
      tool: {
        id: '2',
        name: 'Pressure Washer',
        imageUrl: 'https://images.pexels.com/photos/2244746/pexels-photo-2244746.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
        pricePerDay: 25,
        location: 'Brooklyn, New York',
        rating: 4.7,
        reviews: 18,
      },
      owner: {
        id: 'owner2',
        name: 'Jane Smith',
        avatar: 'https://images.pexels.com/photos/774909/pexels-photo-774909.jpeg?auto=compress&cs=tinysrgb&w=60&h=60&dpr=2',
        rating: 4.8,
      },
      startDate: '2024-01-25',
      endDate: '2024-01-27',
      status: 'upcoming',
      totalPrice: 75,
      bookingId: 'booking2',
      reviewed: false,
      conversationId: 'conv2',
    },
    {
      id: 'bt3',
      tool: {
        id: '3',
        name: 'Circular Saw',
        imageUrl: 'https://images.pexels.com/photos/3846249/pexels-photo-3846249.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
        pricePerDay: 18,
        location: 'Queens, New York',
        rating: 4.3,
        reviews: 9,
      },
      owner: {
        id: 'owner3',
        name: 'Michael Scott',
        avatar: 'https://images.pexels.com/photos/614810/pexels-photo-614810.jpeg?auto=compress&cs=tinysrgb&w=60&h=60&dpr=2',
        rating: 4.6,
      },
      startDate: '2024-01-10',
      endDate: '2024-01-12',
      status: 'completed',
      totalPrice: 54,
      bookingId: 'booking3',
      reviewed: true,
      conversationId: 'conv3',
    },
    {
      id: 'bt4',
      tool: {
        id: '4',
        name: 'Lawn Mower',
        imageUrl: 'https://images.pexels.com/photos/1448353/pexels-photo-1448353.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
        pricePerDay: 22,
        location: 'Bronx, New York',
        rating: 4.4,
        reviews: 8,
      },
      owner: {
        id: 'owner4',
        name: 'Emily Davis',
        avatar: 'https://images.pexels.com/photos/733872/pexels-photo-733872.jpeg?auto=compress&cs=tinysrgb&w=60&h=60&dpr=2',
        rating: 4.7,
      },
      startDate: '2024-01-05',
      endDate: '2024-01-07',
      status: 'completed',
      totalPrice: 66,
      bookingId: 'booking4',
      reviewed: false,
      conversationId: 'conv4',
    },
    {
      id: 'bt5',
      tool: {
        id: '5',
        name: 'Air Compressor',
        imageUrl: 'https://images.pexels.com/photos/8005397/pexels-photo-8005397.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
        pricePerDay: 16,
        location: 'Manhattan, New York',
        rating: 4.2,
        reviews: 7,
      },
      owner: {
        id: 'owner5',
        name: 'Robert Brown',
        avatar: 'https://images.pexels.com/photos/220453/pexels-photo-220453.jpeg?auto=compress&cs=tinysrgb&w=60&h=60&dpr=2',
        rating: 4.5,
      },
      startDate: '2024-01-18',
      endDate: '2024-01-19',
      status: 'overdue',
      totalPrice: 32,
      bookingId: 'booking5',
      reviewed: false,
      conversationId: 'conv5',
    },
  ];
  
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };
  
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <Clock className="h-4 w-4" />;
      case 'upcoming':
        return <Calendar className="h-4 w-4" />;
      case 'completed':
        return <CheckCircle className="h-4 w-4" />;
      case 'cancelled':
        return <XCircle className="h-4 w-4" />;
      case 'overdue':
        return <AlertCircle className="h-4 w-4" />;
      default:
        return <Clock className="h-4 w-4" />;
    }
  };
  
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'upcoming':
        return 'bg-blue-100 text-blue-800';
      case 'completed':
        return 'bg-gray-100 text-gray-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      case 'overdue':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  
  const filteredTools = filter === 'all' 
    ? borrowedTools 
    : borrowedTools.filter(tool => {
        if (filter === 'active') return tool.status === 'active' || tool.status === 'overdue';
        return tool.status === filter;
      });
  
  const stats = {
    total: borrowedTools.length,
    active: borrowedTools.filter(t => t.status === 'active' || t.status === 'overdue').length,
    upcoming: borrowedTools.filter(t => t.status === 'upcoming').length,
    completed: borrowedTools.filter(t => t.status === 'completed').length,
  };

  return (
    <div className="container mx-auto px-4 py-8 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="mb-8">
        <Link to="/dashboard" className="flex items-center text-green-600 mb-4 hover:text-green-700">
          <ArrowLeft className="h-5 w-5 mr-2" />
          <span>Back to dashboard</span>
        </Link>
        <h1 className="text-2xl font-bold text-gray-900">Borrowed Tools</h1>
        <p className="mt-2 text-gray-600">
          Manage your current and past tool rentals
        </p>
      </div>
      
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="text-2xl font-bold text-gray-900">{stats.total}</div>
          <div className="text-sm text-gray-500">Total Rentals</div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="text-2xl font-bold text-green-600">{stats.active}</div>
          <div className="text-sm text-gray-500">Active</div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="text-2xl font-bold text-blue-600">{stats.upcoming}</div>
          <div className="text-sm text-gray-500">Upcoming</div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="text-2xl font-bold text-gray-600">{stats.completed}</div>
          <div className="text-sm text-gray-500">Completed</div>
        </div>
      </div>
      
      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-8">
        <div className="flex flex-wrap items-center gap-4">
          <span className="text-sm font-medium text-gray-700">Filter by status:</span>
          <button
            onClick={() => setFilter('all')}
            className={`px-3 py-1 rounded-full text-sm font-medium ${
              filter === 'all'
                ? 'bg-green-100 text-green-800'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            All ({stats.total})
          </button>
          <button
            onClick={() => setFilter('active')}
            className={`px-3 py-1 rounded-full text-sm font-medium ${
              filter === 'active'
                ? 'bg-green-100 text-green-800'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            Active ({stats.active})
          </button>
          <button
            onClick={() => setFilter('upcoming')}
            className={`px-3 py-1 rounded-full text-sm font-medium ${
              filter === 'upcoming'
                ? 'bg-green-100 text-green-800'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            Upcoming ({stats.upcoming})
          </button>
          <button
            onClick={() => setFilter('completed')}
            className={`px-3 py-1 rounded-full text-sm font-medium ${
              filter === 'completed'
                ? 'bg-green-100 text-green-800'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            Completed ({stats.completed})
          </button>
        </div>
      </div>
      
      {/* Tools List */}
      {filteredTools.length > 0 ? (
        <div className="space-y-6">
          {filteredTools.map((borrowedTool) => (
            <div key={borrowedTool.id} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="flex flex-col md:flex-row">
                {/* Tool Image */}
                <div className="w-full md:w-48 lg:w-64">
                  <Link to={`/tool/${borrowedTool.tool.id}`}>
                    <img 
                      src={borrowedTool.tool.imageUrl} 
                      alt={borrowedTool.tool.name}
                      className="w-full h-48 md:h-full object-cover hover:opacity-90 transition-opacity"
                    />
                  </Link>
                </div>
                
                {/* Content */}
                <div className="flex-1 p-6">
                  <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between">
                    <div className="flex-1">
                      {/* Tool Info */}
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <Link to={`/tool/${borrowedTool.tool.id}`}>
                            <h3 className="text-xl font-bold text-gray-900 hover:text-green-600 transition-colors">
                              {borrowedTool.tool.name}
                            </h3>
                          </Link>
                          <div className="flex items-center text-sm text-gray-500 mt-1">
                            <MapPin className="h-4 w-4 mr-1" />
                            <span>{borrowedTool.tool.location}</span>
                          </div>
                        </div>
                        <Badge 
                          variant={borrowedTool.status === 'overdue' ? 'danger' : 
                                  borrowedTool.status === 'active' ? 'success' :
                                  borrowedTool.status === 'upcoming' ? 'primary' : 'secondary'}
                          className={`ml-4 ${getStatusColor(borrowedTool.status)}`}
                        >
                          <span className="flex items-center">
                            {getStatusIcon(borrowedTool.status)}
                            <span className="ml-1 capitalize">{borrowedTool.status}</span>
                          </span>
                        </Badge>
                      </div>
                      
                      {/* Rental Dates */}
                      <div className="flex items-center text-sm text-gray-600 mb-3">
                        <Calendar className="h-4 w-4 mr-1" />
                        <span>{formatDate(borrowedTool.startDate)} - {formatDate(borrowedTool.endDate)}</span>
                      </div>
                      
                      {/* Owner Info */}
                      <div className="flex items-center mb-4">
                        <img
                          src={borrowedTool.owner.avatar}
                          alt={borrowedTool.owner.name}
                          className="h-8 w-8 rounded-full mr-2"
                        />
                        <div className="text-sm">
                          <span className="text-gray-500">Owner: </span>
                          <span className="text-gray-900 font-medium">{borrowedTool.owner.name}</span>
                          <div className="flex items-center">
                            <Star className="h-3 w-3 text-yellow-500 fill-current mr-1" />
                            <span className="text-gray-500">{borrowedTool.owner.rating}</span>
                          </div>
                        </div>
                      </div>
                      
                      {/* Special Messages */}
                      {borrowedTool.status === 'overdue' && (
                        <div className="bg-red-50 border border-red-200 rounded-md p-3 mb-4">
                          <div className="flex items-center">
                            <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
                            <div>
                              <p className="text-sm font-medium text-red-800">Tool is overdue</p>
                              <p className="text-xs text-red-600">Please return as soon as possible to avoid additional fees.</p>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {borrowedTool.status === 'upcoming' && (
                        <div className="bg-blue-50 border border-blue-200 rounded-md p-3 mb-4">
                          <div className="flex items-center">
                            <Calendar className="h-5 w-5 text-blue-500 mr-2" />
                            <div>
                              <p className="text-sm font-medium text-blue-800">Pickup reminder</p>
                              <p className="text-xs text-blue-600">Don't forget to pick up your tool on {formatDate(borrowedTool.startDate)}.</p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {/* Actions and Price */}
                    <div className="mt-4 lg:mt-0 lg:ml-6 flex flex-col items-end">
                      <div className="text-right mb-4">
                        <div className="text-lg font-bold text-gray-900">
                          ${borrowedTool.totalPrice.toFixed(2)}
                        </div>
                        <div className="text-sm text-gray-500">Total paid</div>
                      </div>
                      
                      <div className="flex flex-col sm:flex-row lg:flex-col gap-2">
                        {borrowedTool.conversationId && (
                          <Link to={`/conversation/${borrowedTool.conversationId}`}>
                            <Button 
                              variant="outline" 
                              size="sm"
                              icon={<MessageSquare className="h-4 w-4 mr-2" />}
                              className="w-full sm:w-auto lg:w-full"
                            >
                              Message Owner
                            </Button>
                          </Link>
                        )}
                        
                        {borrowedTool.status === 'completed' && !borrowedTool.reviewed && (
                          <Button 
                            variant="primary" 
                            size="sm"
                            icon={<Star className="h-4 w-4 mr-2" />}
                            className="w-full sm:w-auto lg:w-full"
                          >
                            Leave Review
                          </Button>
                        )}
                        
                        {borrowedTool.status === 'upcoming' && (
                          <Button 
                            variant="outline" 
                            size="sm"
                            className="w-full sm:w-auto lg:w-full text-red-600 border-red-300 hover:bg-red-50"
                          >
                            Cancel Booking
                          </Button>
                        )}
                        
                        {(borrowedTool.status === 'active' || borrowedTool.status === 'overdue') && (
                          <Button 
                            variant="primary" 
                            size="sm"
                            className="w-full sm:w-auto lg:w-full"
                          >
                            Mark as Returned
                          </Button>
                        )}
                        
                        <Link to={`/tool/${borrowedTool.tool.id}`}>
                          <Button 
                            variant="outline" 
                            size="sm"
                            className="w-full sm:w-auto lg:w-full"
                          >
                            View Tool
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12 bg-white rounded-lg shadow-sm border border-gray-200">
          <Calendar className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            {filter === 'all' ? 'No borrowed tools' : `No ${filter} rentals`}
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            {filter === 'all' 
              ? "You haven't borrowed any tools yet. Start exploring available tools in your area."
              : `You don't have any ${filter} tool rentals.`
            }
          </p>
          <div className="mt-6">
            <Link to="/browse">
              <Button variant="primary">Browse Tools</Button>
            </Link>
          </div>
        </div>
      )}
      
      {/* Quick Actions */}
      {filteredTools.length > 0 && (
        <div className="mt-8 bg-green-50 rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Quick Actions</h3>
          <div className="flex flex-col sm:flex-row gap-4">
            <Link to="/browse">
              <Button variant="primary" className="w-full sm:w-auto">
                Browse More Tools
              </Button>
            </Link>
            <Link to="/history">
              <Button variant="outline" className="w-full sm:w-auto">
                View Full History
              </Button>
            </Link>
            <Link to="/help">
              <Button variant="outline" className="w-full sm:w-auto">
                Need Help?
              </Button>
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};

export default BorrowedTools;